import type { ReactNode } from 'react';
import type { UIInventoryItem } from '@sorrem-client/types';
import { ItemCard } from '@sorrem-client/ui/components';
import { useItemFrames } from '../ItemFrames';
import styles from './WorkbenchItem.module.scss';

interface WorkbenchItemProps {
    readonly item: UIInventoryItem;
    /** Drawn at the end of each affix line on the card, e.g. its reroll link. */
    readonly affixAction?: (index: number) => ReactNode;
}

/** Items shown in 3D (#287): held ones (weapons, shields) by their own model, armor on the character; rings and amulets keep their icons for now. */
function hasFrame(item: UIInventoryItem): boolean {
    return item.equipmentType === 'melee' || item.equipmentType === 'range' || item.equipmentType === 'shield' || item.equipmentType === 'armor';
}

/** The item's card drawn in 3D: its model in the card's art box, turned by dragging. Its stats flash as the chosen level changes. */
function FramedCard({ item, affixAction }: WorkbenchItemProps): JSX.Element {
    const frames = useItemFrames([item]);
    return (
        <div className={styles.workbenchItem}>
            <ItemCard item={item} art={frames.artFor(0)} variant='workbench' affixAction={affixAction} flashChanges />
            {frames.overlay}
        </div>
    );
}

/**
 * The item on the workbench (#287) at the level chosen beside it (as it
 * is, on the Current card): its card, large, with its model in 3D where it
 * has one. Keyed by the item on the workbench, so its stats flash only as
 * the chosen level changes, not when another item is picked.
 */
export function WorkbenchItem(props: WorkbenchItemProps): JSX.Element {
    return hasFrame(props.item) ? <FramedCard {...props} /> : (
        <div className={styles.workbenchItem}>
            <ItemCard item={props.item} variant='workbench' affixAction={props.affixAction} flashChanges />
        </div>
    );
}
